import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const CACHE_TTL_MS = 20_000; // 20s — short enough that plan changes propagate quickly
let _cache = { value: null, fetchedAt: 0, inflight: null };

/**
 * usePlanMatrix — single source of truth for plan → feature inclusion.
 *
 * Reads the `plan_feature_matrix` row from `platform_settings` and exposes:
 *   { matrix, loading, refresh }
 *
 * Cached in-memory for 60s. Components and helpers should not query
 * `platform_settings` directly — go through this hook (or `getPlanMatrix`
 * for non-react contexts).
 */
export default function usePlanMatrix() {
  const [matrix, setMatrix] = useState(_cache.value);
  const [loading, setLoading] = useState(!_cache.value);

  useEffect(() => {
    let active = true;
    const fresh = _cache.value && Date.now() - _cache.fetchedAt < CACHE_TTL_MS;
    if (fresh) {
      setMatrix(_cache.value);
      setLoading(false);
      return;
    }
    getPlanMatrix()
      .then((v) => {
        if (!active) return;
        setMatrix(v);
        setLoading(false);
      })
      .catch(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const refresh = async () => {
    _cache = { value: null, fetchedAt: 0, inflight: null };
    const v = await getPlanMatrix();
    setMatrix(v);
    return v;
  };

  return { matrix, loading, refresh };
}

/**
 * Non-react accessor. Returns a Promise resolving to the matrix value
 * (the JSON in platform_settings.plan_feature_matrix).
 */
export async function getPlanMatrix() {
  const fresh = _cache.value && Date.now() - _cache.fetchedAt < CACHE_TTL_MS;
  if (fresh) return _cache.value;
  if (_cache.inflight) return _cache.inflight;

  _cache.inflight = supabase
    .from("platform_settings")
    .select("value")
    .eq("key", "plan_feature_matrix")
    .maybeSingle()
    .then(({ data, error }) => {
      if (error) {
        // RLS or network failure — log loudly so future regressions are obvious.
        // eslint-disable-next-line no-console
        console.error(
          "[usePlanMatrix] failed to read platform_settings.plan_feature_matrix:",
          error.message || error
        );
      } else if (!data) {
        // eslint-disable-next-line no-console
        console.warn(
          "[usePlanMatrix] platform_settings.plan_feature_matrix returned empty. " +
            "This usually means an RLS policy is blocking the read or the row is missing. " +
            "All Pro features will fail-closed until this is fixed."
        );
      }
      const value = data?.value ?? null;
      _cache = { value, fetchedAt: Date.now(), inflight: null };
      return value;
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error("[usePlanMatrix] unexpected fetch error:", err);
      _cache.inflight = null;
      throw err;
    });

  return _cache.inflight;
}

/** Force-clear cache (used after admin saves the matrix). */
export function invalidatePlanMatrixCache() {
  _cache = { value: null, fetchedAt: 0, inflight: null };
}